import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getPickems } from '@/api/pickems/getPickems';
import { useCreatePickemMutation } from '@/api/pickems/createPickem';
import { useUpdatePickemMutation } from '@/api/pickems/updatePickem';
import { useCompletePickemMutation } from '@/api/pickems/completePickem';
import { useStartScoringMutation } from '@/api/pickems/startScoringAsync';
import { useRecalculatePickemRanksMutation } from '@/api/pickems/recalculatePickemRanks';
import { useDuplicatePickemMutation } from '@/api/pickems/duplicatePickem';
import { useScoringProgress } from '@/hooks/useScoringProgress';
import { ScoringProgressModal } from '@/components/pickems/ScoringProgressModal';
import {
  useGetPickemLeaderboardQuery,
  downloadPickemLeaderboardCSV,
} from '@/api/leaderboard/getPickemLeaderboard';
import { useGetPickemEntriesQuery } from '@/api/pickems/getPickemEntries';
import { useGetLeaguesQuery } from '@/api/leagues/getLeagues';
import { useGetQuestionsQuery } from '@/api/questions/getQuestions';
import { useReorderQuestionsMutation } from '@/api/questions/reorderQuestions';
import { Title } from '@/components/composite/Title';
import { BackButton } from '@/components/composite/buttons/BackButton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { TranslatableFieldLabel } from '@/components/composite/TranslatableFieldLabel';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Download, Plus, ChevronDown, ChevronUp, Trophy, Copy, RefreshCw } from 'lucide-react';
import { SortableQuestionsList } from '@/components/questions/SortableQuestionsList';
import { SelectionsDetail } from '@/components/pickems/SelectionsDetail';
import { PickemTimelineStepper } from '@/components/pickems/PickemTimelineStepper';
import { DuplicatePickemDialog } from '@/components/pickems/DuplicatePickemDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TimezoneTooltip } from '@/components/datetime/TimezoneTooltip';
import { DateDisplay } from '@/components/datetime/DateDisplay';
import { Formik, Form, Field, ErrorMessage, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useFieldTranslation } from '@/hooks/useFieldTranslation';
import { RequestBody, queryKeys } from '@/api/react-query.config';
import { toast } from 'sonner';

// Use API-derived types for type safety
type CreatePickemRequest = RequestBody<'/api/admin/pickems', 'post'>;
type UpdatePickemRequest = RequestBody<'/api/admin/pickems/{id}', 'put'>;

// Form values use Create schema (more lenient with optional fields)
// We handle conversion to Update schema in handleSubmit
type PickemFormValues = CreatePickemRequest;

/**
 * Get the earliest game start time from questions with SportCC matches
 */
function getEarliestGameTime(questions: any[]): Date | null {
  const gameTimes = questions
    .filter(q => q.sportcc_match_data?.matchDate)
    .map(q => new Date(q.sportcc_match_data.matchDate));

  if (gameTimes.length === 0) return null;

  return new Date(Math.min(...gameTimes.map(d => d.getTime())));
}

/**
 * Create validation schema with dynamic validation based on questions
 */
const createPickemValidationSchema = (questions: any[]) => {
  const earliestGame = getEarliestGameTime(questions);

  return Yup.object({
    league_id: Yup.string().required('League is required'),
    title_en: Yup.string()
      .required('English title is required')
      .min(3, 'English title must be at least 3 characters')
      .max(200, 'English title must be less than 200 characters'),
    title_fr: Yup.string()
      .min(3, 'French title must be at least 3 characters')
      .max(200, 'French title must be less than 200 characters'),
    start: Yup.string()
      .required('Start date & time is required')
      .test(
        'start-before-first-game',
        earliestGame
          ? `Start date must be before the first game (${earliestGame.toLocaleString()})`
          : 'Start date is invalid',
        function (value) {
          if (!value || !earliestGame) return true;
          return new Date(value) < earliestGame;
        }
      ),
    allow_selection_start: Yup.string()
      .required('Open date & time is required')
      .test('open-before-start', 'Open date must be before start date', function (value) {
        const { start } = this.parent;
        if (!value || !start) return true;
        return new Date(value) < new Date(start);
      })
      .test(
        'open-before-first-game',
        earliestGame
          ? `Open date must be before the first game (${earliestGame.toLocaleString()})`
          : 'Open date is invalid',
        function (value) {
          if (!value || !earliestGame) return true;
          return new Date(value) < earliestGame;
        }
      ),
    pickem_status: Yup.string(),
    prize_text_en: Yup.string().max(500, 'Prize text must be less than 500 characters'),
    prize_text_fr: Yup.string().max(500, 'Prize text must be less than 500 characters'),
  });
};

export const Route = createFileRoute('/dashboard/pickems/$id')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      league_id: search.league_id as string | undefined,
    };
  },
  loader: async ({ params }) => {
    const { pickems } = await getPickems();
    const pickem = pickems.find(p => p.pickem_id === params.id);
    return { pickem };
  },
  component: RouteComponent,
});

export function RouteComponent() {
  const { pickem } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [questionOrder, setQuestionOrder] = useState<string[] | null>(null);
  const [scoringModalOpen, setScoringModalOpen] = useState(false);
  const [scoringPickemId, setScoringPickemId] = useState<string | null>(pickem?.pickem_id || null);

  const { data: leagues = [], isLoading: leaguesLoading } = useGetLeaguesQuery();
  const { data: allQuestions = [], isLoading: questionsLoading } = useGetQuestionsQuery();

  // Call all hooks before any conditional returns
  const { mutate: createMutate, isPending: createIsPending } = useCreatePickemMutation();
  const { mutate: updateMutate, isPending: updateIsPending } = useUpdatePickemMutation();
  const { mutate: completeMutate, isPending: completeIsPending } = useCompletePickemMutation();
  const { mutate: startScoringMutate, isPending: startScoringIsPending } =
    useStartScoringMutation();
  const { mutate: recalculateRanksMutate, isPending: recalculateRanksIsPending } =
    useRecalculatePickemRanksMutation();
  const { mutate: duplicateMutate, isPending: duplicateIsPending } = useDuplicatePickemMutation();
  const { mutate: reorderQuestions } = useReorderQuestionsMutation();

  // SSE scoring progress hook - always enabled to detect in-progress scoring
  const { progress } = useScoringProgress({
    pickemId: scoringPickemId,
    enabled: !!scoringPickemId,
    onComplete: () => {
      // Invalidate queries when scoring completes - React Query will refetch automatically
      queryClient.invalidateQueries({ queryKey: queryKeys.pickems.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.questions.all });
      if (scoringPickemId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard.pickem(scoringPickemId) });
      }

      // Close modal after delay (no page reload needed - queries will refetch)
      setTimeout(() => {
        setScoringModalOpen(false);
        setScoringPickemId(null);
      }, 2500);
    },
    onError: error => {
      toast.error(`Scoring failed: ${error}`);
    },
  });

  // Auto-open modal if there's in-progress scoring
  useEffect(() => {
    if (progress && progress.status === 'in_progress' && !scoringModalOpen) {
      setScoringModalOpen(true);
    }
  }, [progress, scoringModalOpen]);

  // Always call the hook, but conditionally enable it
  const isPickemComplete = pickem?.status === 'COMPLETE';

  // Check editable flag from API response
  const isPickemEditable = pickem?.editable !== false; // Default to true if not present
  const pickemEditMessage = pickem?.edit_message;

  const { data: leaderboardData, isLoading: leaderboardLoading } = useGetPickemLeaderboardQuery(
    isPickemComplete ? pickem?.pickem_id : undefined
  );

  // Fetch pickem entries
  const { data: entriesData, isLoading: entriesLoading } = useGetPickemEntriesQuery(
    pickem?.pickem_id,
    { limit: 100, offset: 0 }
  );

  // Translation hook
  const { translateField, isFieldTranslating } = useFieldTranslation();

  const pickemQuestions = useMemo(() => {
    return allQuestions.filter(q => q.pickem_id === pickem?.pickem_id);
  }, [allQuestions, pickem?.pickem_id]);

  if (leaguesLoading || questionsLoading) {
    return (
      <Card className="flex flex-col gap-4 w-full max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <Title>Loading...</Title>
            <BackButton />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-muted-foreground">Loading pickem data...</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if all questions have a correct answer marked
  const allQuestionsHaveCorrectAnswer = pickemQuestions.every(q =>
    q.question_type === 'tiebreaker'
      ? q.correct_value !== undefined && q.correct_value !== null
      : q.options?.some(opt => opt.is_correct === true)
  );

  const questionsWithoutCorrectAnswer = pickemQuestions.filter(q =>
    q.question_type === 'tiebreaker'
      ? !(q.correct_value !== undefined && q.correct_value !== null)
      : !q.options?.some(opt => opt.is_correct === true)
  ).length;

  const handleCompletePickem = () => {
    if (!pickem?.pickem_id) return;

    // First mark as complete (which updates status to COMPLETE)
    completeMutate(
      { pickemId: pickem.pickem_id },
      {
        onSuccess: () => {
          // Then start SSE-based scoring
          startScoringMutate(pickem.pickem_id, {
            onSuccess: () => {
              setScoringPickemId(pickem.pickem_id);
              setScoringModalOpen(true);
            },
          });
        },
      }
    );
  };

  const handleDownloadCSV = async () => {
    if (pickem?.pickem_id) {
      try {
        await downloadPickemLeaderboardCSV(pickem.pickem_id);
      } catch (error) {
        console.error('Failed to download CSV:', error);
      }
    }
  };

  const handleForceScoring = async () => {
    if (pickem?.pickem_id) {
      // Use SSE-based scoring
      startScoringMutate(pickem.pickem_id, {
        onSuccess: () => {
          setScoringPickemId(pickem.pickem_id);
          setScoringModalOpen(true);
        },
      });
    }
  };

  const handleRecalculateRanks = async () => {
    if (pickem?.pickem_id) {
      recalculateRanksMutate(pickem.pickem_id);
    }
  };

  const handleDuplicatePickem = (leagueId: string) => {
    if (pickem?.pickem_id) {
      duplicateMutate({ pickemId: pickem.pickem_id, league_id: leagueId });
    }
  };

  const initialValues: PickemFormValues = {
    league_id: pickem?.league_id || search.league_id,
    title_en: pickem?.title_en || '',
    title_fr: pickem?.title_fr,
    start: pickem?.start ? new Date(pickem.start).toISOString().slice(0, 16) : undefined,
    allow_selection_start: pickem?.allow_selection_start
      ? new Date(pickem.allow_selection_start).toISOString().slice(0, 16)
      : undefined,
    pickem_status: pickem?.status,
    prize_text_en: pickem?.prize_text_en,
    prize_text_fr: pickem?.prize_text_fr,
  };

  const handleSubmit = async (values: PickemFormValues, { setSubmitting }: any) => {
    try {
      if (pickem) {
        // Convert to UpdatePickemRequest (requires non-optional fields)
        const updatePayload: UpdatePickemRequest & { id: string } = {
          id: pickem.pickem_id,
          title_en: values.title_en,
          title_fr: values.title_fr,
          league_id: values.league_id,
          start: values.start || new Date().toISOString(),
          allow_selection_start: values.allow_selection_start,
          pickem_status: values.pickem_status,
          prize_text_en: values.prize_text_en || '',
          prize_text_fr: values.prize_text_fr,
          gamezone_image_en: values.gamezone_image_en,
          gamezone_image_fr: values.gamezone_image_fr,
          text_gamezone_image_en: values.text_gamezone_image_en,
          text_gamezone_image_fr: values.text_gamezone_image_fr,
          text_prize_image_en: values.text_prize_image_en,
          text_prize_image_fr: values.text_prize_image_fr,
          text_main_image_en: values.text_main_image_en,
          text_main_image_fr: values.text_main_image_fr,
          prizes_desc_en: values.prizes_desc_en,
          prizes_desc_fr: values.prizes_desc_fr,
          pin_user_ids: values.pin_user_ids,
        };

        await new Promise((resolve, reject) => {
          updateMutate(updatePayload, {
            onSuccess: resolve,
            onError: reject,
          });
        });

        // If question order has changed, save it
        if (questionOrder) {
          await new Promise<void>((resolve, reject) => {
            reorderQuestions(
              { pickemId: pickem.pickem_id, questionIds: questionOrder },
              {
                onSuccess: () => {
                  setQuestionOrder(null); // Reset after successful save
                  resolve();
                },
                onError: reject,
              }
            );
          });
        }
      } else {
        // Type-safe create - values already match CreatePickemRequest
        await new Promise((resolve, reject) => {
          createMutate(values, {
            onSuccess: resolve,
            onError: reject,
          });
        });
      }
    } catch (error) {
      console.error('Failed to save pickem:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="flex flex-col gap-4 w-full max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <Title>{pickem ? 'Edit' : 'Add new'} pickem</Title>
          <div className="flex items-center gap-2">
            {pickem && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDuplicateDialogOpen(true)}
                disabled={duplicateIsPending}
              >
                <Copy className="mr-2 h-4 w-4" />
                Duplicate
              </Button>
            )}
            <BackButton />
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Timeline Stepper - only show for existing pickems */}
        {pickem && (
          <PickemTimelineStepper
            pickem={pickem}
            allQuestionsHaveCorrectAnswer={allQuestionsHaveCorrectAnswer}
            questionsCount={pickemQuestions.length}
          />
        )}

        <Formik
          initialValues={initialValues}
          validationSchema={createPickemValidationSchema(pickemQuestions)}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={true}
        >
          {({
            isSubmitting,
            errors,
            touched,
            values,
            setFieldValue,
          }: FormikProps<PickemFormValues>) => {
            const durationWarning = useMemo(() => {
              if (values.start && values.allow_selection_start) {
                const startDate = new Date(values.start);
                const openDate = new Date(values.allow_selection_start);
                const diffMs = startDate.getTime() - openDate.getTime();
                const diffHours = diffMs / (1000 * 60 * 60);

                if (diffHours > 0 && diffHours < 24) {
                  const hours = Math.floor(diffHours);
                  const minutes = Math.floor((diffHours - hours) * 60);
                  return { hours, minutes };
                }
              }
              return null;
            }, [values.start, values.allow_selection_start]);

            return (
              <Form className="flex flex-col gap-6">
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList
                    className={`grid w-full ${isPickemComplete ? 'grid-cols-5' : pickem ? 'grid-cols-4' : 'grid-cols-1'}`}
                  >
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    {pickem && <TabsTrigger value="questions">Questions</TabsTrigger>}
                    {pickem && <TabsTrigger value="entries">Entries</TabsTrigger>}
                    {isPickemComplete && <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>}
                  </TabsList>

                  <TabsContent value="basic" className="space-y-4">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="league_id">League *</Label>
                      <Select
                        value={values.league_id || ''}
                        onValueChange={value => setFieldValue('league_id', value)}
                        disabled={leaguesLoading || !isPickemEditable}
                      >
                        <SelectTrigger
                          data-test-id="pickem-league_id-select"
                          className={errors.league_id && touched.league_id ? 'border-red-500' : ''}
                        >
                          <SelectValue placeholder="Select a league" />
                        </SelectTrigger>
                        <SelectContent>
                          {leagues.map(league => (
                            <SelectItem key={league.league_id} value={league.league_id}>
                              {league.name_en || league.title || `League #${league.league_id}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <ErrorMessage name="league_id">
                        {msg => <span className="text-sm text-red-500">{msg}</span>}
                      </ErrorMessage>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-2">
                        <TranslatableFieldLabel
                          htmlFor="title_en"
                          onTranslate={() =>
                            translateField('title_en', values.title_en, 'title_fr', setFieldValue)
                          }
                          isTranslating={isFieldTranslating('title_en')}
                        >
                          Title (English)
                        </TranslatableFieldLabel>
                        <Field
                          as={Input}
                          id="title_en"
                          name="title_en"
                          data-test-id="pickem-title_en"
                          placeholder="Enter pickem title in English"
                          readOnly={!isPickemEditable}
                          disabled={!isPickemEditable}
                          className={`${errors.title_en && touched.title_en ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        />
                        <ErrorMessage name="title_en">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label htmlFor="title_fr">Title (French)</Label>
                        <Field
                          as={Input}
                          id="title_fr"
                          name="title_fr"
                          data-test-id="pickem-title_fr"
                          placeholder="Enter pickem title in French"
                          readOnly={isFieldTranslating('title_en') || !isPickemEditable}
                          disabled={!isPickemEditable}
                          className={`${errors.title_fr && touched.title_fr ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        />
                        <ErrorMessage name="title_fr">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-2">
                        <TranslatableFieldLabel
                          htmlFor="prize_text_en"
                          onTranslate={() =>
                            translateField(
                              'prize_text_en',
                              values.prize_text_en || '',
                              'prize_text_fr',
                              setFieldValue
                            )
                          }
                          isTranslating={isFieldTranslating('prize_text_en')}
                        >
                          Prize Text (English)
                        </TranslatableFieldLabel>
                        <Field
                          as={Input}
                          id="prize_text_en"
                          name="prize_text_en"
                          data-test-id="pickem-prize_text_en"
                          placeholder="Enter prize description"
                          readOnly={!isPickemEditable}
                          disabled={!isPickemEditable}
                          className={`${errors.prize_text_en && touched.prize_text_en ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        />
                        <ErrorMessage name="prize_text_en">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label htmlFor="prize_text_fr">Prize Text (French)</Label>
                        <Field
                          as={Input}
                          id="prize_text_fr"
                          name="prize_text_fr"
                          data-test-id="pickem-prize_text_fr"
                          placeholder="Enter prize description"
                          readOnly={isFieldTranslating('prize_text_en') || !isPickemEditable}
                          disabled={!isPickemEditable}
                          className={`${errors.prize_text_fr && touched.prize_text_fr ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        />
                        <ErrorMessage name="prize_text_fr">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>
                    </div>{' '}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-1">
                          <Label htmlFor="allow_selection_start">
                            Open Date & Time
                            <span className="text-muted-foreground text-xs ml-1">(UTC)</span>
                          </Label>
                          {values.allow_selection_start && (
                            <TimezoneTooltip utcDate={values.allow_selection_start} />
                          )}
                        </div>
                        <Field
                          as={Input}
                          type="datetime-local"
                          id="allow_selection_start"
                          name="allow_selection_start"
                          data-test-id="pickem-allow_selection_start"
                          readOnly={!isPickemEditable}
                          disabled={!isPickemEditable}
                          className={`${errors.allow_selection_start && touched.allow_selection_start ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        />
                        <p className="text-xs text-muted-foreground">
                          This is the date/time from when this pickem is visible to users.
                        </p>
                        <ErrorMessage name="allow_selection_start">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-1">
                          <Label htmlFor="start">
                            Start Date & Time
                            <span className="text-muted-foreground text-xs ml-1">(UTC)</span>
                          </Label>
                          {values.start && <TimezoneTooltip utcDate={values.start} />}
                        </div>
                        <Field
                          as={Input}
                          type="datetime-local"
                          id="start"
                          name="start"
                          data-test-id="pickem-start"
                          readOnly={!isPickemEditable}
                          disabled={!isPickemEditable}
                          className={`${errors.start && touched.start ? 'border-red-500' : ''} ${!isPickemEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        />
                        <p className="text-xs text-muted-foreground">
                          After this date/time users will no longer be able to make selections.
                        </p>
                        <ErrorMessage name="start">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                      </div>
                    </div>
                    {/* Status field - only show when editing (readonly) */}
                    {pickem && (
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="pickem_status">Status</Label>
                        <Input
                          id="pickem_status"
                          value={(values.pickem_status || 'ACTIVE') as string}
                          readOnly
                          disabled
                          className="bg-gray-50 cursor-not-allowed"
                          data-test-id="pickem-pickem_status-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Status is automatically managed by the system. Use "Mark as Complete &
                          Score" button in the Questions tab to complete the pickem.
                        </p>
                      </div>
                    )}
                  </TabsContent>

                  {pickem && (
                    <TabsContent value="questions" className="space-y-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-semibold">Questions</h3>
                          <p className="text-sm text-gray-600">
                            Drag and drop to reorder questions
                          </p>
                        </div>
                        {isPickemEditable && (
                          <Button
                            onClick={() =>
                              navigate({
                                to: '/dashboard/questions/$id',
                                params: { id: 'new' },
                                search: { pickem_id: pickem.pickem_id },
                              })
                            }
                            variant="outline"
                            size="sm"
                            type="button"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Question
                          </Button>
                        )}
                      </div>

                      {questionsWithoutCorrectAnswer > 0 && (
                        <Alert className="bg-yellow-50 border-yellow-200">
                          <AlertDescription className="text-yellow-800">
                            {questionsWithoutCorrectAnswer}{' '}
                            {questionsWithoutCorrectAnswer === 1
                              ? 'question needs'
                              : 'questions need'}{' '}
                            a correct answer marked before the pickem can be completed.
                          </AlertDescription>
                        </Alert>
                      )}

                      <SortableQuestionsList
                        questions={pickemQuestions}
                        onOrderChange={setQuestionOrder}
                      />

                      <div className="pt-4 border-t">
                        <Button
                          onClick={handleCompletePickem}
                          disabled={
                            !allQuestionsHaveCorrectAnswer ||
                            isPickemComplete ||
                            completeIsPending ||
                            startScoringIsPending
                          }
                          variant={
                            allQuestionsHaveCorrectAnswer && !isPickemComplete
                              ? 'default'
                              : 'outline'
                          }
                          className="w-full"
                          type="button"
                        >
                          {completeIsPending || startScoringIsPending
                            ? 'Processing...'
                            : isPickemComplete
                              ? 'Pickem Already Completed'
                              : !allQuestionsHaveCorrectAnswer
                                ? `Cannot Complete - ${questionsWithoutCorrectAnswer} Question${questionsWithoutCorrectAnswer === 1 ? '' : 's'} Missing Correct Answer`
                                : 'Mark as Complete & Score'}
                        </Button>
                        {!isPickemComplete && allQuestionsHaveCorrectAnswer && (
                          <p className="text-xs text-center text-gray-500 mt-2">
                            This will change the status to "Completed" and trigger scoring for all
                            participants
                          </p>
                        )}
                      </div>
                    </TabsContent>
                  )}

                  {pickem && (
                    <TabsContent value="entries" className="space-y-4">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h3 className="text-lg font-semibold">Entries</h3>
                          <p className="text-sm text-gray-600">
                            {entriesData?.total || 0} users submitted picks
                          </p>
                        </div>
                      </div>

                      {entriesLoading ? (
                        <div className="text-center py-8">Loading entries...</div>
                      ) : entriesData?.entries && entriesData.entries.length > 0 ? (
                        <div className="border rounded-md overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Username
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Entry Date
                                </th>
                                {isPickemComplete && (
                                  <>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                      Points
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                      Rank
                                    </th>
                                  </>
                                )}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {entriesData.entries.map(entry => (
                                <>
                                  <tr
                                    key={entry.userId}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() =>
                                      setExpandedEntryId(
                                        expandedEntryId === entry.userId ? null : entry.userId
                                      )
                                    }
                                  >
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                      {expandedEntryId === entry.userId ? (
                                        <ChevronUp className="w-4 h-4" />
                                      ) : (
                                        <ChevronDown className="w-4 h-4" />
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                      {entry.username}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      {entry.entryDate ? (
                                        <DateDisplay utcDate={entry.entryDate}>
                                          {new Date(entry.entryDate).toLocaleString()}
                                        </DateDisplay>
                                      ) : (
                                        'N/A'
                                      )}
                                    </td>
                                    {isPickemComplete && (
                                      <>
                                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                          {entry.points !== null ? entry.points : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                          {entry.rank !== null ? `#${entry.rank}` : '-'}
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                  {expandedEntryId === entry.userId && (
                                    <tr key={`${entry.userId}-details`}>
                                      <td colSpan={isPickemComplete ? 5 : 3} className="p-0">
                                        <SelectionsDetail
                                          pickemId={pickem.pickem_id}
                                          userId={entry.userId}
                                          username={entry.username}
                                        />
                                      </td>
                                    </tr>
                                  )}
                                </>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">No entries yet</div>
                      )}
                    </TabsContent>
                  )}

                  {isPickemComplete && (
                    <TabsContent value="leaderboard" className="space-y-4">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h3 className="text-lg font-semibold">Top 500 Leaderboard</h3>
                          <p className="text-sm text-gray-600">
                            {leaderboardData?.selectionsCount || 0} total participants
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {!pickem?.scored && (
                            <Button
                              onClick={handleForceScoring}
                              disabled={startScoringIsPending}
                              className="gap-2"
                              type="button"
                            >
                              <Trophy className="w-4 h-4" />
                              {startScoringIsPending ? 'Scoring...' : 'Score Pickem Now'}
                            </Button>
                          )}
                          {isPickemComplete && (leaderboardData?.leaderboard?.length ?? 0) > 0 && (
                            <Button
                              onClick={handleRecalculateRanks}
                              disabled={recalculateRanksIsPending}
                              variant="outline"
                              className="gap-2"
                              type="button"
                            >
                              <RefreshCw className="w-4 h-4" />
                              {recalculateRanksIsPending ? 'Recalculating...' : 'Recalculate Ranks'}
                            </Button>
                          )}
                          <Button
                            onClick={handleDownloadCSV}
                            variant="outline"
                            className="gap-2"
                            type="button"
                          >
                            <Download className="w-4 h-4" />
                            Export CSV
                          </Button>
                        </div>
                      </div>

                      {leaderboardLoading ? (
                        <div className="text-center py-8">Loading leaderboard...</div>
                      ) : leaderboardData?.leaderboard && leaderboardData.leaderboard.length > 0 ? (
                        <div className="border rounded-md overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Rank
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                  Username
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                  Points
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {leaderboardData.leaderboard.map((entry: any) => (
                                <tr key={entry.user_id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                    {entry.rank}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {entry.username}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                    {entry.points}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          No leaderboard data available
                        </div>
                      )}
                    </TabsContent>
                  )}
                </Tabs>

                {/* Warning for short pickem duration */}
                {durationWarning && (
                  <Alert className="bg-yellow-50 border-yellow-200">
                    <AlertDescription className="text-yellow-800">
                      ⚠️ This pickem will only be available to users for {durationWarning.hours}{' '}
                      hours and {durationWarning.minutes} mins. Consider adjusting the start and
                      open times.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Info message for non-editable pickems */}
                {!isPickemEditable && pickemEditMessage && (
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-blue-800">
                      {pickemEditMessage}
                    </AlertDescription>
                  </Alert>
                )}

                {isPickemEditable && (
                  <Button
                    type="submit"
                    data-test-id="pickem-submit-button"
                    disabled={isSubmitting || createIsPending || updateIsPending}
                    className="w-full"
                  >
                    {isSubmitting || createIsPending || updateIsPending
                      ? 'Saving...'
                      : questionOrder
                        ? 'Save Pickem & Question Order'
                        : 'Save Pickem'}
                  </Button>
                )}
              </Form>
            );
          }}
        </Formik>
      </CardContent>

      {/* Duplicate Pickem Dialog */}
      {pickem && (
        <DuplicatePickemDialog
          open={duplicateDialogOpen}
          onOpenChange={setDuplicateDialogOpen}
          onConfirm={handleDuplicatePickem}
          leagues={leagues}
          isLoading={duplicateIsPending}
        />
      )}

      {/* Scoring Progress Modal */}
      <ScoringProgressModal open={scoringModalOpen} progress={progress} />
    </Card>
  );
}
